type Child = Node | string | number | null | undefined | false;
type Props = Record<string, unknown>;

/** Tiny element factory: h('div', { class: 'x', onClick: fn }, 'text', child). */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props = {},
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value == null || value === false) continue;
    if (key === 'class') {
      el.className = String(value);
    } else if (key === 'style') {
      for (const [prop, v] of Object.entries(value as Record<string, string | number>)) {
        el.style.setProperty(prop, String(v));
      }
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (key === 'value' || key === 'disabled') {
      (el as unknown as Record<string, unknown>)[key] = value;
    } else {
      el.setAttribute(key, value === true ? '' : String(value));
    }
  }
  for (const child of children.flat()) {
    if (child == null || child === false) continue;
    el.append(child instanceof Node ? child : String(child));
  }
  return el;
}

const ICONS = {
  help: '<path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm-1-5h2v2h-2v-2Zm2-1.6V14h-2v-1.5a1 1 0 0 1 1-1 1.5 1.5 0 1 0-1.47-1.8l-1.96-.4A3.5 3.5 0 1 1 13 13.4Z"/>',
  stats: '<path d="M3 12h4v9H3v-9Zm7-8h4v17h-4V4Zm7 4h4v13h-4V8Z"/>',
  camera: '<path d="M9.83 5 8.17 7H4v12h16V7h-4.17l-1.66-2H9.83ZM9 3h6l2 2h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4l2-2Zm3 15a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11Zm0-2a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>',
  grid: '<path d="M3 3h8v8H3V3Zm2 2v4h4V5H5Zm8-2h8v8h-8V3Zm2 2v4h4V5h-4ZM3 13h8v8H3v-8Zm2 2v4h4v-4H5Zm8-2h8v8h-8v-8Zm2 2v4h4v-4h-4Z"/>',
  close: '<path d="m12 10.6 4.95-4.95 1.41 1.41L13.41 12l4.95 4.95-1.41 1.41L12 13.41l-4.95 4.95-1.41-1.41L10.59 12 5.64 7.05l1.41-1.41L12 10.59Z"/>',
  dice: '<path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm0 2v14h14V5H5Zm3.5 5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm7 7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm-3.5-3.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z"/>',
  share: '<path d="M13 14h-2a8.99 8.99 0 0 0-7.75 4.43A10 10 0 0 1 13 8V3l10 8-10 8v-5Zm-2-2h4v2.84L19.8 11 15 7.16V10h-2a7.98 7.98 0 0 0-6.92 4A10.97 10.97 0 0 1 11 12Z"/>',
  bulb: '<path d="M9.97 18h1.03v-5h2v5h1.03c.13-1.2.74-2.19 1.73-3.27l.68-.73A6 6 0 1 0 6 10c0 1.53.6 2.95 1.56 4l.68.73c1 1.08 1.6 2.07 1.73 3.27ZM10 20v1h4v-1h-4Zm-3.95-4.65A8 8 0 1 1 18.3 15.4c-.62.67-1.3 1.65-1.3 2.6v3a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-3c0-.95-.68-1.92-1.3-2.6Z"/>',
  arrow: '<path d="M12 2 22 12h-6v10H8V12H2L12 2Z"/>',
  check: '<path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17Z"/>',
  cross: '<path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41Z"/>',
  enter: '<path d="M16.17 13H4v-2h12.17l-5.36-5.36 1.42-1.42L20 12l-7.78 7.78-1.42-1.42L16.17 13Z"/>',
} as const;

export type IconName = keyof typeof ICONS;

export function icon(name: IconName): HTMLSpanElement {
  const span = h('span', { class: 'icon', 'aria-hidden': 'true' });
  // Static, trusted markup from the table above.
  span.innerHTML = `<svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">${ICONS[name]}</svg>`;
  return span;
}
